import { useCallback, useMemo } from 'react';

import axios from 'axios';

import {
    EmployeeNotification,
    EmployeeNotificationCategory,
    GetEmployeeNotificationsUnreadCountResponseBody,
    ListEmployeeNotificationsQueryParams,
    ListEmployeeNotificationsResponseBody,
    MarkAllEmployeeNotificationsReadResponseBody,
    MarkEmployeeNotificationReadPathParams,
    MarkEmployeeNotificationReadResponseBody,
} from '@sparkplug/lib';

import {
    invalidateQueriesSafe,
    useAdvancedInfiniteQuery,
    useAdvancedMutation,
    useAdvancedQuery,
    useQueryClient,
} from '@hooks/QueryHooks';

const API = {
    list: async (
        params: ListEmployeeNotificationsQueryParams = {},
    ): Promise<ListEmployeeNotificationsResponseBody> =>
        (
            await axios.get<ListEmployeeNotificationsResponseBody>(
                '/api/v1/employee-data/notifications',
                { params },
            )
        ).data,

    getUnreadCount: async (): Promise<GetEmployeeNotificationsUnreadCountResponseBody> =>
        (
            await axios.get<GetEmployeeNotificationsUnreadCountResponseBody>(
                '/api/v1/employee-data/notifications/unread-count',
            )
        ).data,

    markRead: async ({
        notificationId,
    }: MarkEmployeeNotificationReadPathParams): Promise<MarkEmployeeNotificationReadResponseBody> =>
        (
            await axios.post<MarkEmployeeNotificationReadResponseBody>(
                `/api/v1/employee-data/notifications/${notificationId}/read`,
            )
        ).data,

    markAllRead: async (): Promise<MarkAllEmployeeNotificationsReadResponseBody> =>
        (
            await axios.post<MarkAllEmployeeNotificationsReadResponseBody>(
                '/api/v1/employee-data/notifications/read-all',
            )
        ).data,
};

const PAGE_SIZE = 20;

export const employeeNotificationsCacheKey = 'employee-notifications';
export const employeeNotificationsUnreadCountCacheKey = 'employee-notifications-unread-count';

export const getListQueryKey = (category?: EmployeeNotificationCategory) =>
    category
        ? [employeeNotificationsCacheKey, 'list', category]
        : [employeeNotificationsCacheKey, 'list'];

export const useEmployeeNotificationsListQuery = (
    category?: EmployeeNotificationCategory,
    enabled = true,
) => {
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
        useAdvancedInfiniteQuery<ListEmployeeNotificationsResponseBody, Error, number>(
            getListQueryKey(category),
            async (offset) =>
                API.list({
                    offset: offset ?? 0,
                    limit: PAGE_SIZE,
                    category,
                }),
            {
                enabled,
                getNextPageParam: (lastPage: ListEmployeeNotificationsResponseBody) => {
                    const { offset, limit, total } = lastPage.meta;
                    const nextOffset = offset + limit;
                    return nextOffset < total ? nextOffset : undefined;
                },
            },
        );

    const notifications: EmployeeNotification[] = useMemo(
        () => data?.pages?.flatMap((page) => page.data) ?? [],
        [data],
    );

    const total = data?.pages?.[0]?.meta?.total ?? 0;

    const loadMore = useCallback(() => {
        if (!isFetchingNextPage && hasNextPage) {
            fetchNextPage();
        }
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    return {
        notifications,
        total,
        isLoading,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        loadMore,
        refetch,
    };
};

/**
 * Lightweight unread-count query for the bottom-nav badge. Refetches on window
 * focus so the badge stays in sync if a notification arrives in another tab or
 * is delivered while the app is backgrounded.
 */
export const useEmployeeNotificationsUnreadCountQuery = (enabled = true) => {
    const { data, isLoading } = useAdvancedQuery<{ data: { unreadCount: number } }, Error>(
        employeeNotificationsUnreadCountCacheKey,
        () => API.getUnreadCount(),
        {
            enabled,
            refetchOnWindowFocus: true,
            // Tighter than the global default; the badge should feel live.
            staleTime: 30 * 1000,
        },
    );

    return {
        unreadCount: data?.data?.unreadCount ?? 0,
        isLoading,
    };
};

export const useMarkEmployeeNotificationReadMutation = () => {
    const queryClient = useQueryClient();

    return useAdvancedMutation<
        unknown,
        { notificationId: string },
        { data: { success: true } },
        Error,
        unknown
    >(({ notificationId }) => API.markRead({ notificationId }), {
        customOptions: {
            onSettled: () => {
                // Invalidate every list variant (with/without category) plus the badge.
                invalidateQueriesSafe(queryClient, employeeNotificationsCacheKey);
                invalidateQueriesSafe(queryClient, employeeNotificationsUnreadCountCacheKey);
            },
        },
    });
};

export const useMarkAllEmployeeNotificationsReadMutation = () => {
    const queryClient = useQueryClient();

    return useAdvancedMutation<
        unknown,
        void,
        { data: { success: true; updatedCount: number } },
        Error,
        unknown
    >(() => API.markAllRead(), {
        customOptions: {
            onSettled: () => {
                invalidateQueriesSafe(queryClient, employeeNotificationsCacheKey);
                invalidateQueriesSafe(queryClient, employeeNotificationsUnreadCountCacheKey);
            },
        },
    });
};
